import { createImageElement } from '../core/documentSchema';

const normalizedRotation = (rotation) => (((rotation || 0) % 360) + 360) % 360;

export const rotatedImageIntersectsCrop = (element, cropRect) => {
  const rad = (element.rotation * Math.PI) / 180;
  const cos = Math.abs(Math.cos(rad));
  const sin = Math.abs(Math.sin(rad));
  const halfW = (element.width / 2) * Math.abs(element.scaleX);
  const halfH = (element.height / 2) * Math.abs(element.scaleY);
  const boundsW = halfW * cos + halfH * sin;
  const boundsH = halfW * sin + halfH * cos;
  const left = element.x - boundsW;
  const top = element.y - boundsH;
  const right = element.x + boundsW;
  const bottom = element.y + boundsH;

  return (
    right > cropRect.x &&
    left < cropRect.x + cropRect.width &&
    bottom > cropRect.y &&
    top < cropRect.y + cropRect.height
  );
};

export const shouldRasterizeForCrop = (element) =>
  element.type === 'image' && element.image && normalizedRotation(element.rotation) !== 0;

export const rasterizeRotatedImage = async (element, cropRect) => {
  const rad = (element.rotation * Math.PI) / 180;
  const cos = Math.abs(Math.cos(rad));
  const sin = Math.abs(Math.sin(rad));
  const halfW = (element.width / 2) * Math.abs(element.scaleX);
  const halfH = (element.height / 2) * Math.abs(element.scaleY);
  const boundsW = halfW * cos + halfH * sin;
  const boundsH = halfW * sin + halfH * cos;
  const intersectionX = Math.max(cropRect.x, element.x - boundsW);
  const intersectionY = Math.max(cropRect.y, element.y - boundsH);
  const intersectionRight = Math.min(cropRect.x + cropRect.width, element.x + boundsW);
  const intersectionBottom = Math.min(cropRect.y + cropRect.height, element.y + boundsH);
  const width = Math.round(intersectionRight - intersectionX);
  const height = Math.round(intersectionBottom - intersectionY);

  if (width < 1 || height < 1) {
    return null;
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.translate(-intersectionX, -intersectionY);
  ctx.translate(element.x, element.y);
  ctx.rotate(rad);
  ctx.scale(element.scaleX, element.scaleY);

  const { naturalWidth, naturalHeight } = element;
  const crop = element.crop;
  const sx = crop ? crop.x : 0;
  const sy = crop ? crop.y : 0;
  const sw = crop ? crop.width : naturalWidth;
  const sh = crop ? crop.height : naturalHeight;

  ctx.drawImage(element.image, sx, sy, sw, sh, -element.width / 2, -element.height / 2, element.width, element.height);

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob((result) => {
      if (result) {
        resolve(result);
      } else {
        reject(new Error('Failed to rasterize crop'));
      }
    }, 'image/png');
  });

  const objectUrl = URL.createObjectURL(blob);
  try {
    const bitmap = await createImageBitmap(blob);
    return createImageElement(
      {
        image: bitmap,
        objectUrl,
        naturalWidth: bitmap.width,
        naturalHeight: bitmap.height,
        name: element.name,
      },
      {
        id: element.id,
        name: element.name,
        x: intersectionX - cropRect.x + width / 2,
        y: intersectionY - cropRect.y + height / 2,
        width,
        height,
        opacity: element.opacity,
        blendMode: element.blendMode,
        rotation: 0,
        scaleX: 1,
        scaleY: 1,
        visible: element.visible,
        locked: element.locked,
        filters: element.filters,
        keepAspectRatio: element.keepAspectRatio,
        crop: null,
      },
    );
  } catch (error) {
    URL.revokeObjectURL(objectUrl);
    throw error;
  }
};
